import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import {
    getAllProfiles,
    getSmsConfigurations,
    linkAnswerToProfile,
    Profile,
    SmsConfiguration,
    unlinkAnswerFromProfile,
    updateSmsConfiguration,
} from '@/lib/forward-api'
import { Answer, useCustomerStore } from '@/stores/customer-store'
import { Forward, Phone, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface AnswerEditPanelProps {
    answer: Answer
    onClose: () => void
    onSave: (answer: Answer, customerText?: string, useSystemDefault?: boolean) => Promise<void>
}

interface AnswerVariant {
    id: string
    content: string
    defaultAnswer?: boolean
}

interface ConversationMessage {
    id: number
    isCustomer: boolean
    text: string
}

export function AnswerEditPanel({ answer, onClose, onSave }: AnswerEditPanelProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const { loadSystemAnswers, loadCallHandlingProfiles } = useCustomerStore()
    const [isSaving, setIsSaving] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('answer')

    // Determine if it's ANSWER or OPTION type
    const isAnswerType = !!answer.answerVariants

    // System answers loaded from backend
    const [systemAnswers, setSystemAnswers] = useState<AnswerVariant[]>([])

    // Forwarding state
    const [profiles, setProfiles] = useState<Profile[]>([])
    const [selectedProfileId, setSelectedProfileId] = useState<string>('-1')
    const [forwardingLoading, setForwardingLoading] = useState(false)

    // SMS state
    const [smsConfig, setSmsConfig] = useState<SmsConfiguration>({
        activeOnAction: '',
        messageToSend: '',
        utteranceToSay: '',
        smsActive: false,
    })
    const [smsLoading, setSmsLoading] = useState(false)

    // State for selected variant
    const [selectedVariantId, setSelectedVariantId] = useState<string>(() => {
        if (answer.overriden) {
            return 'custom'
        }
        if (answer.answerVariants && answer.answerVariants.length > 0) {
            return answer.answerVariants[0].id
        }
        return 'system'
    })

    // Custom answer text
    const [customAnswerText, setCustomAnswerText] = useState<string>(() => {
        if (isAnswerType) {
            // ANSWER type
            if (answer.overriden && answer.answerVariants && answer.answerVariants.length > 0) {
                return answer.answerVariants[0].content
            }
        } else {
            // OPTION type
            if (answer.enabled && answer.customerAnswerActive) {
                return answer.customerAnswerActive
            } else if (!answer.enabled && answer.customerAnswerInactive) {
                return answer.customerAnswerInactive
            }
        }
        return ''
    })

    // Load forwarding profiles when answer changes (ANSWER type only)
    useEffect(() => {
        if (isAnswerType && answer.id) {
            loadForwardingData()
            loadSmsData()
        }
    }, [answer.id, isAnswerType])

    const loadForwardingData = async () => {
        if (!answer.id) return
        setForwardingLoading(true)
        try {
            const allProfiles = await getAllProfiles()
            setProfiles(allProfiles)
            const linkedProfile = allProfiles.find((p) => p.usedByActions?.includes(answer.id))
            setSelectedProfileId(linkedProfile?.id || '-1')
        } catch (error) {
            console.error('Failed to load profiles:', error)
        } finally {
            setForwardingLoading(false)
        }
    }

    const loadSmsData = async () => {
        if (!answer.id) return
        setSmsLoading(true)
        try {
            const configs = await getSmsConfigurations()
            const config = configs.find((c) => c.activeOnAction === answer.id)
            if (config) {
                setSmsConfig(config)
            } else {
                setSmsConfig({
                    activeOnAction: answer.id,
                    messageToSend: '',
                    utteranceToSay: '',
                    smsActive: false,
                })
            }
        } catch (error) {
            console.error('Failed to load SMS config:', error)
        } finally {
            setSmsLoading(false)
        }
    }

    const handleForwardingChange = async (profileId: string) => {
        if (!answer.id) return
        setForwardingLoading(true)
        try {
            if (profileId === '-1') {
                await unlinkAnswerFromProfile(answer.id)
                toast({
                    title: t('success.title'),
                    description: t('answers.edit.forwarding.forwardingEnabled.description'),
                })
            } else {
                await linkAnswerToProfile(answer.id, profileId)
                toast({
                    title: t('success.title'),
                    description: t('answers.edit.forwarding.forwardingEnabled.title'),
                })
            }
            setSelectedProfileId(profileId)
            loadCallHandlingProfiles()
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('answerEdit.saveError'),
                variant: 'destructive',
            })
        } finally {
            setForwardingLoading(false)
        }
    }

    const handleSmsUpdate = async () => {
        if (!answer.id) return
        setSmsLoading(true)
        try {
            await updateSmsConfiguration({
                ...smsConfig,
                activeOnAction: answer.id,
            })
            toast({
                title: t('success.title'),
                description: t('answerEdit.saveSuccess'),
            })
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('answerEdit.saveError'),
                variant: 'destructive',
            })
        } finally {
            setSmsLoading(false)
        }
    }

    // Load system answers on mount
    useEffect(() => {
        const loadAnswers = async () => {
            try {
                setIsLoading(true)

                if (isAnswerType) {
                    // ANSWER type: Load from systemanswers API
                    const systemAnswersData = await loadSystemAnswers(answer.id)
                    if (systemAnswersData.answerVariants) {
                        setSystemAnswers(systemAnswersData.answerVariants)

                        // Auto-select the default answer if not overridden
                        if (!answer.overriden) {
                            const defaultAnswer = systemAnswersData.answerVariants.find((v: AnswerVariant) => v.defaultAnswer)
                            if (defaultAnswer) {
                                setSelectedVariantId(defaultAnswer.id)
                            }
                        }
                    }
                } else {
                    // OPTION type: Use defaultAnswerActive/Inactive from answer object
                    const variants: AnswerVariant[] = []

                    if (answer.defaultAnswerActive) {
                        variants.push({
                            id: 'active',
                            content: answer.defaultAnswerActive,
                            defaultAnswer: true
                        })
                    }

                    if (answer.defaultAnswerInactive) {
                        variants.push({
                            id: 'inactive',
                            content: answer.defaultAnswerInactive,
                            defaultAnswer: false
                        })
                    }

                    setSystemAnswers(variants)

                    // Auto-select based on enabled state
                    if (answer.enabled) {
                        setSelectedVariantId('active')
                    } else if (variants.length > 0) {
                        setSelectedVariantId(variants[0].id)
                    }
                }
            } catch (error) {
                console.error('Failed to load system answers:', error)
                toast({
                    title: t('error.title'),
                    description: t('answerEdit.loadSystemAnswersError'),
                    variant: 'destructive'
                })
            } finally {
                setIsLoading(false)
            }
        }

        if (answer.id) {
            loadAnswers()
        } else {
            setIsLoading(false)
        }
    }, [answer.id, isAnswerType, loadSystemAnswers, answer.overriden, answer.enabled, answer.defaultAnswerActive, answer.defaultAnswerInactive])

    // Get the current answer text based on selection
    const getCurrentAnswerText = (): string => {
        if (selectedVariantId === 'custom') {
            return customAnswerText
        }

        // Find selected system answer
        const selectedAnswer = systemAnswers.find(a => a.id === selectedVariantId)
        if (selectedAnswer) {
            return selectedAnswer.content
        }

        // Fallback to current answer variant
        if (answer.answerVariants && answer.answerVariants.length > 0) {
            return answer.answerVariants[0].content
        }

        return ''
    }

    // Parse example conversation from backend format
    const parseExampleConversation = (): ConversationMessage[] => {
        if (!answer.exampleConversation) {
            return []
        }

        const lines = answer.exampleConversation.split('\r\n')
        const messages: ConversationMessage[] = []
        let id = 0

        // Add initial "..." message
        messages.push({
            id: id++,
            isCustomer: true,
            text: '...'
        })

        const currentAnswerText = getCurrentAnswerText()

        for (const line of lines) {
            const match = line.match(/^([UA]):\s(.+)$/)
            if (match) {
                const [, speaker, text] = match
                let messageText = text.trim()

                // Replace <SYSTEMANSWER> with actual selected answer
                if (messageText === '<SYSTEMANSWER>') {
                    messageText = currentAnswerText
                }

                messages.push({
                    id: id++,
                    isCustomer: speaker === 'U',
                    text: messageText
                })
            }
        }

        return messages
    }

    const conversationMessages = parseExampleConversation()

    const handleUseSystemAnswer = (systemAnswer: AnswerVariant) => {
        setCustomAnswerText(systemAnswer.content)
        setSelectedVariantId('custom')
    }

    const handleSave = async () => {
        setIsSaving(true)
        try {
            const useSystemDefault = selectedVariantId !== 'custom'
            const customerText = selectedVariantId === 'custom' ? customAnswerText : ''

            await onSave(answer, customerText, useSystemDefault)

            toast({
                title: t('success.title'),
                description: t('answerEdit.saveSuccess'),
            })
        } catch (error) {
            toast({
                title: t('error.title'),
                description: t('answerEdit.saveError'),
                variant: 'destructive'
            })
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex items-center justify-between">
                <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">
                        {answer.title || answer.displayTitle || t('answerEdit.editAnswer')}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {answer.id?.startsWith('temp_') ? t('answerEdit.newAnswer') : t('answerEdit.editAnswer')}
                    </p>
                </div>
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-900 flex-shrink-0"
                >
                    <X className="h-5 w-5" />
                </Button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
                {isAnswerType ? (
                    <div className="h-full flex flex-col">
                        {/* Tab Navigation - underline style matching profile-detail-view */}
                        <div className="px-6 pt-4 border-b border-gray-200">
                            <div className="flex gap-6">
                                <button
                                    onClick={() => setActiveTab('answer')}
                                    className={`pb-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'answer'
                                        ? 'border-gray-900 text-gray-900'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    {t('answers.edit.areas.answers')}
                                </button>
                                <button
                                    onClick={() => setActiveTab('forwarding')}
                                    className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'forwarding'
                                        ? 'border-gray-900 text-gray-900'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    <Forward className="h-3.5 w-3.5" />
                                    {t('answers.edit.areas.forwarding')}
                                    {answer.forwardingEnabled && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                    )}
                                </button>
                                <button
                                    onClick={() => setActiveTab('sms')}
                                    className={`pb-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${activeTab === 'sms'
                                        ? 'border-gray-900 text-gray-900'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    <Phone className="h-3.5 w-3.5" />
                                    {t('answers.edit.areas.sms')}
                                    {answer.smsEnabled && (
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Tab Content */}
                        {activeTab === 'answer' && (
                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                {/* Forwarding/SMS Active Inline Badges */}
                                {(answer.forwardingEnabled || answer.smsEnabled) && (
                                    <div className="flex flex-wrap gap-2">
                                        {answer.forwardingEnabled && (
                                            <Badge
                                                variant="outline"
                                                className="cursor-pointer bg-green-50 text-green-700 border-green-200 hover:bg-green-100 transition-colors"
                                                onClick={() => setActiveTab('forwarding')}
                                            >
                                                <Forward className="h-3 w-3 mr-1" />
                                                {t('answers.edit.answer.forwardingEnabled.title')}
                                            </Badge>
                                        )}
                                        {answer.smsEnabled && (
                                            <Badge
                                                variant="outline"
                                                className="cursor-pointer bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-colors"
                                                onClick={() => setActiveTab('sms')}
                                            >
                                                <Phone className="h-3 w-3 mr-1" />
                                                {t('answers.edit.sms.activateToggle')}
                                            </Badge>
                                        )}
                                    </div>
                                )}
                                {/* Example Conversation */}
                                {conversationMessages.length > 0 && (
                                    <Card className="border-2 border-brand-primary-light bg-gradient-to-br from-white to-brand-primary-light/20">
                                        <CardContent className="p-6">
                                            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                <div className="w-1.5 h-5 bg-brand-primary rounded-full"></div>
                                                {t('answerEdit.exampleConversation')}
                                            </h3>
                                            <div className="space-y-3 bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                                {conversationMessages.map((message) => (
                                                    <div key={message.id} className={`flex ${message.isCustomer ? 'justify-start' : 'justify-end'}`}>
                                                        <div className={`flex gap-2 max-w-[85%] ${message.isCustomer ? 'flex-row' : 'flex-row-reverse'}`}>
                                                            {/* Avatar */}
                                                            {message.isCustomer ? (
                                                                <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-xs font-medium flex-shrink-0 shadow-sm">
                                                                    K
                                                                </div>
                                                            ) : (
                                                                <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0 shadow-sm">
                                                                    AI
                                                                </div>
                                                            )}

                                                            {/* Message Bubble */}
                                                            <div className="flex flex-col">
                                                                <div className={`
                                                    px-4 py-2.5 rounded-2xl shadow-sm
                                                    ${message.isCustomer
                                                                        ? 'bg-gray-800 text-white rounded-tl-none'
                                                                        : 'bg-brand-primary text-white rounded-tr-none'
                                                                    }
                                                `}>
                                                                    <p className="text-sm leading-relaxed">{message.text}</p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}

                                                {/* Current Selected Answer */}
                                                <div className="mt-4 pt-4 border-t border-gray-200">
                                                    <div className="flex justify-end">
                                                        <div className="flex gap-2 max-w-[85%] flex-row-reverse">
                                                            {/* AI Avatar */}
                                                            <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0 shadow-sm">
                                                                AI
                                                            </div>

                                                            {/* Current Answer Bubble */}
                                                            <div className="flex flex-col gap-1">
                                                                <div className="px-4 py-2.5 rounded-2xl shadow-sm bg-brand-primary text-white rounded-tr-none">
                                                                    <p className="text-sm leading-relaxed">{getCurrentAnswerText() || t('answerEdit.noAnswerSelected')}</p>
                                                                </div>
                                                                <p className="text-xs text-gray-500 text-right italic">{t('answerEdit.currentlySelected')}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <p className="text-xs text-gray-500 mt-3 italic">
                                                {t('answerEdit.exampleConversationHint')}
                                            </p>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Answer Variant Selection */}
                                {isLoading ? (
                                    <div className="flex items-center justify-center py-8">
                                        <div className="flex flex-col items-center gap-3">
                                            <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent"></div>
                                            <p className="text-sm text-gray-500">{t('answerEdit.loadingVariants')}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div>
                                        <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('answerEdit.selectVariant')}</h3>
                                        <RadioGroup
                                            value={selectedVariantId}
                                            onValueChange={setSelectedVariantId}
                                            className="space-y-3"
                                        >
                                            {/* System Answers */}
                                            {systemAnswers.map((systemAnswer, index) => (
                                                <div key={systemAnswer.id} className="flex items-start space-x-3">
                                                    <RadioGroupItem value={systemAnswer.id} id={systemAnswer.id} className="mt-1" />
                                                    <div className="flex-1 space-y-2">
                                                        <div className="flex items-center justify-between">
                                                            <Label htmlFor={systemAnswer.id} className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                                                                {systemAnswer.defaultAnswer ? (
                                                                    <>
                                                                        {t('answerEdit.default')}
                                                                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-primary-light text-brand-primary">
                                                                            {t('answerEdit.recommended')}
                                                                        </span>
                                                                    </>
                                                                ) : (
                                                                    `Template ${index}`
                                                                )}
                                                            </Label>
                                                            {answer.editableByCustomer && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => handleUseSystemAnswer(systemAnswer)}
                                                                    className="h-7 text-xs"
                                                                >
                                                                    {t('answerEdit.useAsTemplate')}
                                                                </Button>
                                                            )}
                                                        </div>
                                                        <Textarea
                                                            value={systemAnswer.content}
                                                            disabled
                                                            rows={3}
                                                            className="text-sm bg-gray-50"
                                                        />
                                                    </div>
                                                </div>
                                            ))}

                                            {/* Custom Answer */}
                                            {answer.editableByCustomer && (
                                                <div className="flex items-start space-x-3">
                                                    <RadioGroupItem value="custom" id="custom" className="mt-1" />
                                                    <div className="flex-1 space-y-2">
                                                        <Label htmlFor="custom" className="font-medium text-gray-900 cursor-pointer">
                                                            {t('answerEdit.customAnswer')}
                                                        </Label>
                                                        <p className="text-sm text-gray-500">
                                                            {t('answerEdit.customAnswerHint')}
                                                        </p>
                                                        {selectedVariantId === 'custom' && (
                                                            <div className="mt-2">
                                                                <Textarea
                                                                    value={customAnswerText}
                                                                    onChange={(e) => setCustomAnswerText(e.target.value)}
                                                                    placeholder={t('answerEdit.customAnswerPlaceholder')}
                                                                    rows={4}
                                                                    className="text-sm"
                                                                />
                                                                <p className="text-xs text-gray-500 mt-1">
                                                                    {t('answerEdit.customAnswerTip')}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </RadioGroup>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Forwarding Tab */}
                        {activeTab === 'forwarding' && (
                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                <div className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                        {t('answers.edit.forwarding.selectionIntro')}
                                    </p>

                                    {forwardingLoading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-brand-primary rounded-full" />
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <Label>{t('answers.edit.areas.forwarding')}</Label>
                                                <Select value={selectedProfileId} onValueChange={handleForwardingChange}>
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={t('answers.edit.forwarding.selectNone')} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="-1">{t('answers.edit.forwarding.selectNone')}</SelectItem>
                                                        {profiles.map((profile) => (
                                                            <SelectItem key={profile.id} value={profile.id}>
                                                                {profile.title}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>

                                            {selectedProfileId !== '-1' && (
                                                <Card className="border-green-200 bg-green-50">
                                                    <CardContent className="p-4">
                                                        <div className="flex items-center gap-2 text-green-700">
                                                            <Forward className="h-4 w-4" />
                                                            <span className="font-medium">{t('answers.edit.forwarding.forwardingEnabled.title')}</span>
                                                        </div>
                                                        <p className="text-sm text-green-600 mt-1">
                                                            {t('answers.edit.forwarding.forwardingEnabled.description')}
                                                        </p>

                                                        {/* Timeslot summary of linked profile */}
                                                        {(() => {
                                                            const linkedProfile = profiles.find((p) => p.id === selectedProfileId)
                                                            if (!linkedProfile?.timeslots) return null

                                                            const weekdays = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']
                                                            const weekdayLabels: Record<string, string> = {
                                                                MONDAY: 'Mo',
                                                                TUESDAY: 'Di',
                                                                WEDNESDAY: 'Mi',
                                                                THURSDAY: 'Do',
                                                                FRIDAY: 'Fr',
                                                                SATURDAY: 'Sa',
                                                                SUNDAY: 'So',
                                                            }

                                                            const timeslotEntries = weekdays
                                                                .filter((day) => {
                                                                    const slots = linkedProfile.timeslots?.[day]
                                                                    return slots && slots.length > 0
                                                                })
                                                                .map((day) => ({
                                                                    day: weekdayLabels[day],
                                                                    slots: linkedProfile.timeslots![day],
                                                                }))

                                                            if (timeslotEntries.length === 0) return null

                                                            return (
                                                                <div className="mt-3 space-y-1">
                                                                    {timeslotEntries.map((entry) =>
                                                                        entry.slots.map((slot: any) => (
                                                                            <div key={slot.id} className="text-xs text-green-700 flex items-center gap-2">
                                                                                <span className="font-medium w-6">{entry.day}</span>
                                                                                <span>
                                                                                    {slot.startTime || slot.timeStart} {t('answers.edit.forwarding.to')} {slot.endTime || slot.timeEnd}
                                                                                </span>
                                                                                {slot.receiver?.title && (
                                                                                    <span className="text-green-600">→ {slot.receiver.title}</span>
                                                                                )}
                                                                            </div>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            )
                                                        })()}
                                                    </CardContent>
                                                </Card>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* SMS Tab */}
                        {activeTab === 'sms' && (
                            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                                <div className="space-y-4">
                                    <p className="text-sm text-muted-foreground">
                                        {t('answers.edit.sms.intro')}
                                    </p>

                                    {smsLoading ? (
                                        <div className="flex items-center justify-center py-8">
                                            <div className="animate-spin h-6 w-6 border-2 border-gray-300 border-t-brand-primary rounded-full" />
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="flex items-center justify-between">
                                                <div className="space-y-1">
                                                    <Label>{t('answers.edit.sms.activateToggle')}</Label>
                                                </div>
                                                <Switch
                                                    checked={smsConfig.smsActive}
                                                    onCheckedChange={(checked) =>
                                                        setSmsConfig((prev) => ({ ...prev, smsActive: checked }))
                                                    }
                                                />
                                            </div>

                                            {smsConfig.smsActive && (
                                                <>
                                                    <div className="space-y-2">
                                                        <Label>{t('answers.edit.sms.labelAnswer')}</Label>
                                                        <Textarea
                                                            value={smsConfig.utteranceToSay}
                                                            onChange={(e) =>
                                                                setSmsConfig((prev) => ({ ...prev, utteranceToSay: e.target.value }))
                                                            }
                                                            placeholder={t('answers.edit.sms.labelAnswer')}
                                                            rows={2}
                                                        />
                                                        <p className="text-xs text-muted-foreground">
                                                            {t('answers.edit.sms.requiredAnswer')}
                                                        </p>
                                                    </div>

                                                    <div className="space-y-2">
                                                        <Label>{t('answers.edit.sms.labelSmsText')}</Label>
                                                        <Textarea
                                                            value={smsConfig.messageToSend}
                                                            onChange={(e) =>
                                                                setSmsConfig((prev) => ({ ...prev, messageToSend: e.target.value }))
                                                            }
                                                            placeholder={t('answers.edit.sms.labelSmsText')}
                                                            rows={3}
                                                        />
                                                        <p className="text-xs text-muted-foreground">
                                                            {t('answers.edit.sms.requiredSmsContent')}
                                                        </p>
                                                    </div>

                                                    <Button onClick={handleSmsUpdate} disabled={smsLoading}>
                                                        {smsLoading ? t('global.button.saving') : t('global.button.save')}
                                                    </Button>
                                                </>
                                            )}

                                            {!smsConfig.smsActive && smsConfig.smsActive !== undefined && (
                                                <Button onClick={handleSmsUpdate} disabled={smsLoading} variant="outline">
                                                    {smsLoading ? t('global.button.saving') : t('global.button.save')}
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    /* OPTION type: No tabs, just the answer content */
                    <div className="p-6 space-y-6">
                        {/* Example Conversation */}
                        {conversationMessages.length > 0 && (
                            <Card className="border-2 border-brand-primary-light bg-gradient-to-br from-white to-brand-primary-light/20">
                                <CardContent className="p-6">
                                    <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <div className="w-1.5 h-5 bg-brand-primary rounded-full"></div>
                                        {t('answerEdit.exampleConversation')}
                                    </h3>
                                    <div className="space-y-3 bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                                        {conversationMessages.map((message) => (
                                            <div key={message.id} className={`flex ${message.isCustomer ? 'justify-start' : 'justify-end'}`}>
                                                <div className={`flex gap-2 max-w-[85%] ${message.isCustomer ? 'flex-row' : 'flex-row-reverse'}`}>
                                                    {message.isCustomer ? (
                                                        <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-white text-xs font-medium flex-shrink-0 shadow-sm">
                                                            K
                                                        </div>
                                                    ) : (
                                                        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0 shadow-sm">
                                                            AI
                                                        </div>
                                                    )}
                                                    <div className="flex flex-col">
                                                        <div className={`px-4 py-2.5 rounded-2xl shadow-sm ${message.isCustomer ? 'bg-gray-800 text-white rounded-tl-none' : 'bg-brand-primary text-white rounded-tr-none'}`}>
                                                            <p className="text-sm leading-relaxed">{message.text}</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Answer Variant Selection */}
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <div className="flex flex-col items-center gap-3">
                                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-primary border-t-transparent"></div>
                                    <p className="text-sm text-gray-500">{t('answerEdit.loadingVariants')}</p>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('answerEdit.selectVariant')}</h3>
                                <RadioGroup value={selectedVariantId} onValueChange={setSelectedVariantId} className="space-y-3">
                                    {systemAnswers.map((systemAnswer, index) => (
                                        <div key={systemAnswer.id} className="flex items-start space-x-3">
                                            <RadioGroupItem value={systemAnswer.id} id={`opt-${systemAnswer.id}`} className="mt-1" />
                                            <div className="flex-1 space-y-2">
                                                <div className="flex items-center justify-between">
                                                    <Label htmlFor={`opt-${systemAnswer.id}`} className="font-medium text-gray-900 cursor-pointer flex items-center gap-2">
                                                        {systemAnswer.defaultAnswer ? (
                                                            <>
                                                                {t('answerEdit.default')}
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-brand-primary-light text-brand-primary">
                                                                    {t('answerEdit.recommended')}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            `Template ${index}`
                                                        )}
                                                    </Label>
                                                    {answer.editableByCustomer && (
                                                        <Button variant="outline" size="sm" onClick={() => handleUseSystemAnswer(systemAnswer)} className="h-7 text-xs">
                                                            {t('answerEdit.useAsTemplate')}
                                                        </Button>
                                                    )}
                                                </div>
                                                <Textarea value={systemAnswer.content} disabled rows={3} className="text-sm bg-gray-50" />
                                            </div>
                                        </div>
                                    ))}
                                    {answer.editableByCustomer && (
                                        <div className="flex items-start space-x-3">
                                            <RadioGroupItem value="custom" id="opt-custom" className="mt-1" />
                                            <div className="flex-1 space-y-2">
                                                <Label htmlFor="opt-custom" className="font-medium text-gray-900 cursor-pointer">
                                                    {t('answerEdit.customAnswer')}
                                                </Label>
                                                <p className="text-sm text-gray-500">{t('answerEdit.customAnswerHint')}</p>
                                                {selectedVariantId === 'custom' && (
                                                    <div className="mt-2">
                                                        <Textarea value={customAnswerText} onChange={(e) => setCustomAnswerText(e.target.value)} placeholder={t('answerEdit.customAnswerPlaceholder')} rows={4} className="text-sm" />
                                                        <p className="text-xs text-gray-500 mt-1">{t('answerEdit.customAnswerTip')}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </RadioGroup>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t flex items-center justify-between bg-gray-50">
                <Button variant="outline" onClick={onClose}>
                    {t('global.button.cancel')}
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                    {isSaving ? t('global.button.saving') : t('global.button.save')}
                </Button>
            </div>
        </div>
    )
}
